import { Variable } from 'domain/models';

/**
 * Color palette for atmospheric variable:
 * - Temperature
 * - Precipitation
 * - Humidity
 * - Wind
 * - Wind gust
 */
export const varColorRange: {
	[key in Variable]: string[];
} = {
	tas: ['#0571b0', '#92c5de', '#f4a582', '#ca0020'],
	pr: ['#018571', '#41b6c4', '#35978f', '#80cdc1'],
	hurs: ['#a1dab4', '#80cdc1', '#2c7fb8', '#253494'],
	sfcwind: ['#deebf7', '#c6dbef', '#9ecae1', '#6baed6', '#3182bd', '#08519c'],
	wgust: ['#c7e9b4', '#7fcdbb', '#41b6c4', '#1d91c0', '#225ea8', '#0c2c84'],
	ir: ['#c994c7', '#df65b0', '#e7298a', '#ce1256', '#980043', '#67001f'],
	leafwetness: [
		'#c994c7',
		'#df65b0',
		'#e7298a',
		'#ce1256',
		'#980043',
		'#67001f',
	],
	tas_cumulative: ['#0571b0', '#92c5de', '#f4a582', '#ca0020'],
	pr_cumulative: ['#018571', '#41b6c4', '#35978f', '#80cdc1'],
	t_hour_cumulative: ['#0571b0', '#92c5de', '#f4a582', '#ca0020'],
};

export const VariableColor = {
	['tas']: varColorRange['tas'][3],
	['pr']: varColorRange['pr'][3],
	['hurs']: varColorRange['hurs'][2],
	['sfcwind']: varColorRange['sfcwind'][5],
	['wgust']: varColorRange['wgust'][5],
	['ir']: varColorRange['ir'][5],
	['leafwetness']: varColorRange['leafwetness'][5],
};

const VarRange: {
	[key in Variable]: [number, number];
} = {
	['tas']: [-20, 40],
	['pr']: [0, 100],
	['hurs']: [0, 100],
	['sfcwind']: [0, 60],
	['wgust']: [0, 80],
	['ir']: [0, 10],
	['leafwetness']: [0, 86400],
	['tas_cumulative']: [0, 0],
	['pr_cumulative']: [0, 0],
	['t_hour_cumulative']: [0, 0],
};

/**
 * Generate a color given a value and a variable
 */
export const getColor = (value: number, variable: Variable) => {
	const colors = varColorRange[variable];
	if (!colors) {
		return '#000000';
	}
	const n = colors.length - 1;
	// Get the i such that range[i] <= value < range[i+1]
	if (!(variable in VarRange)) {
		return colors[0];
	}

	const from = VarRange[variable][0];
	const to = VarRange[variable][1];
	if (from === to) {
		return colors[0];
	}
	const i = Math.floor((n * (value - from)) / (to - from));
	const factor = Math.min(
		Math.max((n * (value - from)) / (to - from) - i, 0),
		1
	);
	if (i < n) {
		return interpolateColor(colors[i], colors[i + 1], factor);
	}
	return colors[i];
};
const interpolateColor = (color1: string, color2: string, factor: number) => {
	const color1Match = color1.slice(1).match(/.{2}/g);
	const color2Match = color2.slice(1).match(/.{2}/g);

	if (!color1Match || !color2Match) {
		throw new Error('Invalid color format');
	}

	const [r1, g1, b1] = color1Match.map((hex) => parseInt(hex, 16));
	const [r2, g2, b2] = color2Match.map((hex) => parseInt(hex, 16));

	const r = Math.round(r1 + factor * (r2 - r1));
	const g = Math.round(g1 + factor * (g2 - g1));
	const b = Math.round(b1 + factor * (b2 - b1));

	return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};
